import type { SortDescriptor } from "react-stately";

import { useState } from "react";

import { BrandsKickstarterBanner } from "@/components/kickstarter-banner/banners";
import { Loader } from "@/components/Loader";
import { useDebounce } from "@/hooks/useDebounce";
import { getPageSizeOptions } from "@/utils/getPageSizeOptions";

import { BrandsTable } from "./components/BrandsTable";
import { Header } from "./shared/components/Header";
import { SearchBar } from "./shared/components/SearchBar";
import { useGetBrandsList } from "./shared/hooks/useGetBrandsList";

export function BrandsPage() {
  const [inputValue, setInputValue] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "createdAt",
    direction: "descending",
  });

  const debouncedQuery = useDebounce(inputValue, 500, () => setPage(1));

  const direction: "asc" | "desc" =
    sortDescriptor.direction === "descending" ? "desc" : "asc";

  const { data: response, isFetching } = useGetBrandsList({
    direction,
    itemsPerPage,
    page,
    search: debouncedQuery,
    sortBy: sortDescriptor?.column as string,
  });

  const brands = response?.brands || [];
  const metadata = response?.metadata;

  return (
    <div className="mx-auto min-h-screen bg-bg-tertiary p-4">
      <div className="mx-auto flex flex-col @container">
        <>
          <BrandsKickstarterBanner />
          <Header />
          <SearchBar inputValue={inputValue} setInputValue={setInputValue} />
          {isFetching ? (
            <Loader className="py-20" />
          ) : (
            <BrandsTable
              brands={brands}
              isLoading={isFetching}
              pagination={
                metadata
                  ? {
                      metadata: {
                        currentPage: metadata.currentPage,
                        itemsPerPage: metadata.itemsPerPage,
                        totalItems: metadata.totalItems,
                        totalPages: metadata.totalPages,
                      },
                      onItemsPerPageChange: (newItemsPerPage) => {
                        setItemsPerPage(newItemsPerPage);
                        setPage(1);
                      },
                      onPageChange: setPage,
                      pageSizeOptions: getPageSizeOptions(metadata.totalItems),
                      showPageSizeSelector: true,
                    }
                  : undefined
              }
              setSortDescriptor={setSortDescriptor}
              sortDescriptor={sortDescriptor}
            />
          )}
        </>
      </div>
    </div>
  );
}
